import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import RefundTermStartDateSource from 'models/enums/RefundTermStartDateSource';
import Insurer from 'models/productManagement/insurer/Model.Insurer';
import PricingDistributionMasterTemplate from 'models/productManagement/pricingDistributionMasterTemplate/Model.PricingDistributionMasterTemplate';
import Product from 'models/productManagement/product/Model.Product';
import refundConfigTemplateDeleteApi from 'models/productManagement/refundConfigTemplate/delete/Api.RefundConfigTemplate.Delete';
import RefundConfigTemplate from 'models/productManagement/refundConfigTemplate/Model.RefundConfigTemplate';
import refundConfigTemplateSaveApi from 'models/productManagement/refundConfigTemplate/save/Api.RefundConfigTemplate.Save';
import Subfee from 'models/productManagement/subfee/Model.Subfee';
import { useMemo } from 'react';
import { refundConfigTemplateIndexRoute } from './RefundConfigTemplate.Index';

export enum RefundConfigTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}

export enum RefundTypes {
  FULL_PULLBACK = 'Full Pullback',
  PRO_RATED = 'Pro Rated',
  UNTOUCHED = 'Untouched',
}

export enum KeyNames {
  MASTER_KEY = 'masterKey',
  INSURER_KEY = 'insurerKey',
  SURCHARGE_KEY = 'surchargeKey',
}

export enum KeyTypeNames {
  MASTER_KEY_TYPE = 'masterKeyType',
  INSURER_KEY_TYPE = 'insurerKeyType',
  SURCHARGE_KEY_TYPE = 'surchargeKeyType',
}

export type VerifyRefundConfigTemplateAction = Utils.VerifyExtends<
  Module.ProductManagement.RefundConfigTemplate.Actions,
  Utils.ValueOf<typeof RefundConfigTemplateAction>
>;

export default function useRefundConfigTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.RefundConfigTemplate.Params,
    Module.ProductManagement.RefundConfigTemplate.Query
  >({
    route: refundConfigTemplateIndexRoute,
  });

  const canView =
    permissions.VIEW_REFUNDCONFIGTEMPLATE ||
    permissions.CREATE_REFUNDCONFIGTEMPLATE ||
    permissions.EDIT_REFUNDCONFIGTEMPLATE ||
    permissions.DELETE_REFUNDCONFIGTEMPLATE;

  const listApi = useApi({
    action: RefundConfigTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset, orderBy: 'internalName', orderType: 'asc' },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const masterTemplatesApi = useApi({
    action: PricingDistributionMasterTemplate.list,
  });

  const insurersApi = useApi({
    action: Insurer.list,
  });

  const surchargesApi = useApi({
    action: Subfee.list,
  });

  const productApi = useApi({
    action: Product.list,
  });

  const masterTemplatesSelectItems = masterTemplatesApi?.payload?.data?.map((it) => ({ id: it.name, title: it.name }));
  const insurersSelectionItems = insurersApi?.payload?.data?.map((it) => ({ id: it.distributionKey, title: it.name }));
  const surchargesSelectionItems = surchargesApi?.payload?.data?.map((it) => ({ id: it.name, title: it.name }));
  const productsSelectionItems = productApi?.payload?.data?.map((it) => it.displayInfo);

  const refundTypesSelectItems = Object.values(RefundTypes).map((name) => ({
    id: name,
    title: name,
  }));

  const termStartDateSourceLabels: Record<RefundTermStartDateSource, string> = {
    [RefundTermStartDateSource.ContractEffectiveDate]: 'Contract Effective Date',
    [RefundTermStartDateSource.ContractCreatedAtLocal]: 'Contract Created At Local',
  };

  const termStartDateSourceSelectItems = Object.values(RefundTermStartDateSource).map((name) => ({
    id: name,
    title: termStartDateSourceLabels[name],
  }));

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: refundConfigTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: refundConfigTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  function syncBy(
    dict: Record<string, string> | undefined | null,
    items: Array<{ id: string; title: string }> | undefined | null,
    by: 'id' | 'title'
  ): Record<string, string> | undefined {
    const safeDict = dict ?? {};
    const safeItems = Array.isArray(items) ? items : [];

    if (!safeItems.length && !Object.keys(safeDict).length) return safeDict;

    const allowed = new Set(
      safeItems
        .map((it) =>
          by === 'id'
            ? String(it?.id ?? '').trim()
            : by === 'title'
              ? String(it?.title ?? '').trim()
              : String(it?.id ?? '').trim()
        )
        .filter((x) => !!x)
    );

    let changed = false;
    const result: Record<string, string> = {};

    for (const [k, v] of Object.entries(safeDict)) {
      if (allowed.has(k)) {
        result[k] = v;
      } else {
        changed = true;
      }
    }

    const originalKeys = Object.keys(safeDict);
    const resultKeys = Object.keys(result);

    if (!changed && originalKeys.length === resultKeys.length) {
      let allSame = true;
      for (const k of originalKeys) {
        if (safeDict[k] !== result[k]) {
          allSame = false;
          break;
        }
      }
      if (allSame) return dict;
    }

    return result;
  }

  const upsertKeyFD = (
    dict: Record<string, string> | undefined,
    keyValue: string,
    typeValue: string
  ): Record<string, string> => {
    const result: Record<string, string> = { ...(dict ?? {}) };
    const k = String(keyValue ?? '').trim();
    const v = String(typeValue ?? '').trim();

    if (!k) return result;

    if (!v) {
      delete result[k];
    } else {
      result[k] = v;
    }

    return result;
  };

  const safeArr = <T>(x: T[] | undefined | null): T[] => (Array.isArray(x) ? x : []);
  const hasOption = (list: { id: any }[] | undefined, id: any) => !!list?.some((o) => String(o.id) === String(id));

  return {
    canView,
    pageRouter,
    listApi,
    masterTemplatesSelectItems,
    productsSelectionItems,
    refundTypesSelectItems,
    termStartDateSourceSelectItems,
    insurersSelectionItems,
    surchargesSelectionItems,
    syncBy,
    upsertKeyFD,
    safeArr,
    hasOption,
    deleteApi,
    saveApi,
    selected,
    permissions,
    views: {
      [RefundConfigTemplateAction.DELETE]:
        pageRouter.params.action === RefundConfigTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_REFUNDCONFIGTEMPLATE,
      [RefundConfigTemplateAction.EDIT]:
        pageRouter.params.action === RefundConfigTemplateAction.EDIT &&
        ((true && !selected) || (true && selected)) &&
        (permissions.EDIT_REFUNDCONFIGTEMPLATE || permissions.CREATE_REFUNDCONFIGTEMPLATE),
    },
  };
}

export function useRefundConfigTemplateProvider() {
  return useDataProvider<ReturnType<typeof useRefundConfigTemplate>>();
}
